import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * PaymentSetup
 * 
 * Vendor-facing payment configuration. Handles:
 *   - Displaying Stripe Connect onboarding status
 *   - Starting the Stripe onboarding flow
 *   - Showing requirements from Stripe
 *   - Managing the Express Dashboard link
 *   - Displaying Pickup Pay eligibility
 * 
 * Used by both Producer and Hub dashboards.
 */
export default function PaymentSetup() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  // If we've just returned from Stripe, force a sync
  const justReturned = searchParams.get('onboarding') === 'complete' ||
                       searchParams.get('onboarding') === 'refresh';

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    setLoading(true);
    setError(null);
    try {
      // If just returned from Stripe, force a sync first
      if (justReturned) {
        await fetch('/api/payments/connect/sync', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      const res = await fetch('/api/payments/connect/status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!res.ok) throw new Error('Failed to load payment status');
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Error loading payment status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function startOnboarding() {
    setActionLoading(true);
    setError(null);
    try {
      // 1. Create (or retrieve) the Stripe connected account
      const accountRes = await fetch('/api/payments/connect/account', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!accountRes.ok) {
        const err = await accountRes.json();
        throw new Error(err.error || 'Failed to create Stripe account');
      }

      // 2. Generate the onboarding link
      const linkRes = await fetch('/api/payments/connect/onboarding-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!linkRes.ok) {
        const err = await linkRes.json();
        throw new Error(err.error || 'Failed to create onboarding link');
      }

      const { url } = await linkRes.json();

      // 3. Redirect to Stripe
      window.location.href = url;
    } catch (err) {
      console.error('Error starting onboarding:', err);
      setError(err.message);
      setActionLoading(false);
    }
  }

  async function openDashboard() {
    setActionLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/payments/connect/dashboard-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!res.ok) throw new Error('Failed to generate dashboard link');
      const { url } = await res.json();
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening dashboard:', err);
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  }

  // ─── Loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-gray-500">
          <span>⏳</span>
          <span className="text-sm">Loading payment status...</span>
        </div>
      </div>
    );
  }

  // ─── Error ──────────────────────────────────────────────────────────
  if (error && !status) {
    return (
      <div className="p-6 bg-red-50 rounded-lg border border-red-200">
        <p className="text-sm text-red-700">⚠️ {error}</p>
        <button
          onClick={loadStatus}
          className="mt-3 text-sm text-red-700 underline hover:text-red-900"
        >
          Try again
        </button>
      </div>
    );
  }

  // ─── Not started ────────────────────────────────────────────────────
  if (status.onboardingStatus === 'not_started' || !status.hasStripeAccount) {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💳</span>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Set up payments</h3>
            <p className="text-sm text-gray-600 mt-1">
              Set up payments to receive card payments and become eligible for Pickup Pay.
              Stripe handles the verification, so this takes just a few minutes.
            </p>
            <button
              onClick={startOnboarding}
              disabled={actionLoading}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {actionLoading ? 'Setting up...' : 'Start setup'}
            </button>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ─── Pending / In progress ──────────────────────────────────────────
  if (status.onboardingStatus === 'pending' || status.onboardingStatus === 'in_progress') {
    return (
      <div className="p-6 bg-white rounded-lg border border-gray-200">
        <div className="flex items-start gap-3">
          <span className="text-2xl">⏳</span>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Setup in progress</h3>
            <p className="text-sm text-gray-600 mt-1">
              Your Stripe setup is in progress. Complete the remaining steps to activate payments.
            </p>

            {status.requirementsDue?.length > 0 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
                <p className="text-xs font-medium text-yellow-800 mb-1">
                  Additional information needed:
                </p>
                <ul className="text-xs text-yellow-700 list-disc list-inside">
                  {status.requirementsDue.slice(0, 5).map((req, i) => (
                    <li key={i}>{req.replace(/_/g, ' ')}</li>
                  ))}
                  {status.requirementsDue.length > 5 && (
                    <li>...and {status.requirementsDue.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}

            <button
              onClick={startOnboarding}
              disabled={actionLoading}
              className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm font-medium"
            >
              {actionLoading ? 'Opening...' : 'Continue setup'}
            </button>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  // ─── Restricted ─────────────────────────────────────────────────────
  if (status.onboardingStatus === 'restricted') {
    return (
      <div className="p-6 bg-white rounded-lg border border-red-200">
        <div className="flex items-start gap-3">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Action required</h3>
            <p className="text-sm text-gray-600 mt-1">
              Stripe has flagged your account for additional review. Please complete the
              requested information to restore payments.
            </p>
            <button
              onClick={startOnboarding}
              disabled={actionLoading}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
            >
              {actionLoading ? 'Opening...' : 'Resolve issues'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Complete ───────────────────────────────────────────────────────
  if (status.onboardingStatus === 'complete') {
    return (
      <div className="space-y-4">
        {/* Status card */}
        <div className="p-6 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-start gap-3">
            <span className="text-2xl">✅</span>
            <div className="flex-1">
              <h3 className="font-semibold text-green-900">Payments active</h3>
              <p className="text-sm text-green-700 mt-1">
                Your account is set up to receive payments.
              </p>
            </div>
          </div>
        </div>

        {/* Accepted payment methods */}
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Accepted payment methods</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className={status.acceptedPaymentMethods?.card ? 'text-green-600' : 'text-gray-400'}>
                {status.acceptedPaymentMethods?.card ? '✅' : '⬜'}
              </span>
              <span className="text-gray-700">Card payments (via Stripe)</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className={status.acceptedPaymentMethods?.pickupPay ? 'text-green-600' : 'text-gray-400'}>
                {status.acceptedPaymentMethods?.pickupPay ? '✅' : '⬜'}
              </span>
              <span className="text-gray-700">
                Pickup Pay (AUDD)
                {status.pickupPayEligible && (
                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Eligible
                  </span>
                )}
              </span>
            </div>
          </div>

          {!status.acceptedPaymentMethods?.pickupPay && status.pickupPayEligible && (
            <p className="mt-3 text-xs text-gray-500">
              Pickup Pay is available for your account. Contact support to enable it.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={openDashboard}
            disabled={actionLoading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 text-sm font-medium"
          >
            {actionLoading ? 'Opening...' : 'Manage in Stripe'}
          </button>
          <button
            onClick={loadStatus}
            disabled={loading}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 text-sm"
          >
            Refresh status
          </button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  // ─── Fallback ───────────────────────────────────────────────────────
  return (
    <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
      <p className="text-sm text-gray-600">
        Unknown payment status: {status.onboardingStatus}
      </p>
    </div>
  );
}
